import React, { useRef, useEffect, useCallback } from "react";
import * as THREE from "three";
import { DesignElement } from "./types";
import { productCustomizationApi } from "../../services/productCustomizationApi";

// Toggle to switch between old OrbitControls zoom and new mouse-position zoom
// Set to false to use OrbitControls default zoom (old behavior)
// Set to true to use custom mouse-position zoom (new behavior)
const USE_MOUSE_POSITION_ZOOM = false; // TEMPORARILY DISABLED FOR TESTING

export interface Product3DPreviewProps {
  elements: DesignElement[];
  backgroundColor: string;
  backgroundImage?: string;
  canvasSize: string;
}

export const Product3DPreview: React.FC<Product3DPreviewProps> = ({
  elements,
  backgroundColor,
  backgroundImage,
  canvasSize,
}) => {
  const mountRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<{
    scene: THREE.Scene;
    camera: THREE.PerspectiveCamera;
    renderer: THREE.WebGLRenderer;
    controls?: any;
    productMesh?: THREE.Mesh;
  } | null>(null);

  // Helper function to draw elements on canvas
  const drawElements = useCallback(
    (
      ctx: CanvasRenderingContext2D,
      elements: DesignElement[],
      canvasWidth: number,
      canvasHeight: number,
      imageNaturalWidth?: number,
      imageNaturalHeight?: number
    ): Promise<void> => {
      // Calculate stretch factors to transform elements from fitted image space to stretched image space
      // In 2D designer: image is fitted to canvas (aspect ratio preserved, centered)
      // In 3D viewer: image is stretched to fill canvas completely
      // Elements are stored in full canvas coordinates but appear on the fitted image
      // We need to map them from fitted image space to stretched (full canvas) space
      let stretchX = 1;
      let stretchY = 1;
      let fittedOffsetX = 0;
      let fittedOffsetY = 0;
      let fittedWidth = canvasWidth;
      let fittedHeight = canvasHeight;
      
      if (imageNaturalWidth && imageNaturalHeight && imageNaturalWidth > 0 && imageNaturalHeight > 0) {
        // Calculate how image is fitted in 2D designer (matching DesignCanvas logic)
        const canvasAspect = canvasWidth / canvasHeight;
        const imageAspect = imageNaturalWidth / imageNaturalHeight;
        
        if (imageAspect > canvasAspect) {
          // Image is wider - fits to width in 2D
          fittedWidth = canvasWidth;
          fittedHeight = canvasWidth / imageAspect;
          fittedOffsetY = (canvasHeight - fittedHeight) / 2; // Center vertically
        } else {
          // Image is taller - fits to height in 2D
          fittedHeight = canvasHeight;
          fittedWidth = canvasHeight * imageAspect;
          fittedOffsetX = (canvasWidth - fittedWidth) / 2; // Center horizontally
        }
        
        // Calculate stretch from fitted size to stretched (full canvas) size
        stretchX = canvasWidth / fittedWidth;
        stretchY = canvasHeight / fittedHeight;
      }
      // Sort elements by zIndex
      const sortedElements = [...elements].sort((a, b) => a.zIndex - b.zIndex);

      // We'll need to handle async image loading for shapes
      const imagePromises: Promise<void>[] = [];

      sortedElements.forEach((element) => {
        ctx.save();

        // Transform element coordinates from fitted image space to stretched image space
        // In 2D designer: Image is fitted (maintains aspect ratio) within preset canvas
        //   - Fitted image area: [fittedOffsetX, fittedOffsetY] to [fittedOffsetX+fittedWidth, fittedOffsetY+fittedHeight]
        //   - White gaps (letterboxing/pillarboxing) exist outside fitted image area
        //   - Elements are positioned in canvas coordinates, but user positions them relative to the VISIBLE fitted image
        // In 3D viewer: Image is stretched to fill entire canvas [0, 0] to [canvasWidth, canvasHeight]
        //   - No gaps, image covers full canvas
        //   - Elements must map from their position relative to fitted image to position relative to stretched image
        
        // Calculate element's position relative to the fitted image bounds
        // If element is within fitted image area, map proportionally
        // If element is outside (in white gap area), handle appropriately
        const elementXRelativeToFitted = element.position.x - fittedOffsetX;
        const elementYRelativeToFitted = element.position.y - fittedOffsetY;
        
        // Map proportionally from fitted image dimensions to stretched (full canvas) dimensions
        // This preserves the element's relative position within the image content
        const normalizedX = fittedWidth > 0 ? elementXRelativeToFitted / fittedWidth : 0;
        const normalizedY = fittedHeight > 0 ? elementYRelativeToFitted / fittedHeight : 0;
        
        // Map normalized position to full stretched canvas coordinates
        const stretchedX = normalizedX * canvasWidth;
        const stretchedY = normalizedY * canvasHeight;
        
        // Apply same proportional scaling to element size
        // Size is also in canvas coordinates, so scale it the same way as position
        const stretchedWidth = (element.size.width / fittedWidth) * canvasWidth;
        const stretchedHeight = (element.size.height / fittedHeight) * canvasHeight;

        // Apply transformations
        const centerX = stretchedX + stretchedWidth / 2;
        const centerY = stretchedY + stretchedHeight / 2;
        ctx.translate(centerX, centerY);
        ctx.rotate(((element.style.rotation || 0) * Math.PI) / 180);
        ctx.translate(-centerX, -centerY);

        // Apply opacity
        ctx.globalAlpha = element.style.opacity ?? 1;

        switch (element.type) {
          case "text":
            ctx.fillStyle = element.style.color || "#000000";
            const fontStyle = element.style.fontStyle || "normal";
            const fontWeight = element.style.fontWeight || "normal";
            // Scale font size proportionally with stretch (use average for better appearance)
            const fontStretch = (stretchX + stretchY) / 2;
            const fontSize = (element.style.fontSize || 24) * fontStretch;
            const fontFamily =
              element.style.fontFamily ||
              "var(--store-font-family, 'Inter', sans-serif)";
            ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;
            ctx.textAlign =
              (element.style.textAlign as CanvasTextAlign) || "left";
            ctx.textBaseline = "top";
            ctx.fillText(
              element.content || "Text",
              stretchedX,
              stretchedY
            );
            break;

          case "shape":
            if (element.content) {
              // Capture transformation state for async image loading (using stretched coordinates)
              const centerX = stretchedX + stretchedWidth / 2;
              const centerY = stretchedY + stretchedHeight / 2;
              const rotation = ((element.style.rotation || 0) * Math.PI) / 180;
              const opacity = element.style.opacity ?? 1;

              // Use backend API to fetch image (bypasses CORS)
              const imgPromise = productCustomizationApi
                .fetchImageAsDataURL(element.content)
                .then((dataURL) => {
                  return new Promise<void>((resolveImg) => {
                    const img = new Image();
                    img.onload = () => {
                      // Apply transformations before drawing (context was restored by outer loop)
                      ctx.save();
                      ctx.translate(centerX, centerY);
                      ctx.rotate(rotation);
                      ctx.translate(-centerX, -centerY);
                      ctx.globalAlpha = opacity;

                      ctx.drawImage(
                        img,
                        stretchedX,
                        stretchedY,
                        stretchedWidth,
                        stretchedHeight
                      );
                      ctx.restore();
                      resolveImg();
                    };
                    img.onerror = () => {
                      // Draw placeholder if image fails
                      ctx.save();
                      ctx.translate(centerX, centerY);
                      ctx.rotate(rotation);
                      ctx.translate(-centerX, -centerY);
                      ctx.globalAlpha = opacity;
                      ctx.fillStyle = "#cccccc";
                      ctx.fillRect(
                        stretchedX,
                        stretchedY,
                        stretchedWidth,
                        stretchedHeight
                      );
                      ctx.restore();
                      resolveImg();
                    };
                    img.src = dataURL;
                  });
                })
                .catch(() => {
                  // Draw placeholder if fetch fails
                  ctx.save();
                  ctx.translate(centerX, centerY);
                  ctx.rotate(rotation);
                  ctx.translate(-centerX, -centerY);
                  ctx.globalAlpha = opacity;
                  ctx.fillStyle = "#cccccc";
                  ctx.fillRect(
                    element.position.x,
                    element.position.y,
                    element.size.width,
                    element.size.height
                  );
                  ctx.restore();
                  return Promise.resolve();
                });
              imagePromises.push(imgPromise);
            }
            break;

          case "frame":
            ctx.strokeStyle = element.style.borderColor || "#000000";
            const borderStretch = (stretchX + stretchY) / 2;
            ctx.lineWidth = (element.style.borderWidth || 2) * borderStretch;
            const borderRadius = (element.style.borderRadius || 0) * Math.min(stretchX, stretchY);
            if (borderRadius > 0) {
              ctx.beginPath();
              const x = stretchedX;
              const y = stretchedY;
              const w = stretchedWidth;
              const h = stretchedHeight;
              ctx.moveTo(x + borderRadius, y);
              ctx.lineTo(x + w - borderRadius, y);
              ctx.quadraticCurveTo(x + w, y, x + w, y + borderRadius);
              ctx.lineTo(x + w, y + h - borderRadius);
              ctx.quadraticCurveTo(x + w, y + h, x + w - borderRadius, y + h);
              ctx.lineTo(x + borderRadius, y + h);
              ctx.quadraticCurveTo(x, y + h, x, y + h - borderRadius);
              ctx.lineTo(x, y + borderRadius);
              ctx.quadraticCurveTo(x, y, x + borderRadius, y);
              ctx.closePath();
              ctx.stroke();
            } else {
              ctx.strokeRect(
                stretchedX,
                stretchedY,
                stretchedWidth,
                stretchedHeight
              );
            }
            break;
        }

        ctx.restore();
      });

      // Return promise that resolves when all images are loaded
      return Promise.all(imagePromises).then(() => {});
    },
    []
  );

  // Function to render the 2D design to a canvas texture
  const renderDesignToTexture = useCallback(
    (
      width: number,
      height: number,
      elements: DesignElement[],
      backgroundColor: string,
      backgroundImage?: string
    ): Promise<THREE.Texture> => {
      return new Promise((resolve) => {
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          throw new Error("Could not get canvas context");
        }

        // Draw background
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, width, height);

        // Draw background image if provided
        if (backgroundImage) {
          // Use backend API to fetch image (bypasses CORS)
          productCustomizationApi
            .fetchImageAsDataURL(backgroundImage)
            .then(async (dataURL) => {
              const img = new Image();
              img.onload = async () => {
                // Redraw background color first (in case image has transparency)
                ctx.fillStyle = backgroundColor;
                ctx.fillRect(0, 0, width, height);
                
                // Stretch image to fill entire canvas for 3D viewer (different from 2D designer)
                // This ensures the image covers the whole 3D object surface
                // Pass image natural dimensions so elements can stretch proportionally
                ctx.drawImage(img, 0, 0, width, height);
                // Draw elements on top (wait for all images to load)
                // Pass image natural dimensions so elements stretch with the background image
                await drawElements(ctx, elements, width, height, img.naturalWidth, img.naturalHeight);
                const texture = new THREE.CanvasTexture(canvas);
                texture.needsUpdate = true;
                resolve(texture);
              };
              img.onerror = async () => {
                await drawElements(ctx, elements, width, height);
                const texture = new THREE.CanvasTexture(canvas);
                texture.needsUpdate = true;
                resolve(texture);
              };
              img.src = dataURL;
            })
            .catch(async () => {
              await drawElements(ctx, elements, width, height);
              const texture = new THREE.CanvasTexture(canvas);
              texture.needsUpdate = true;
              resolve(texture);
            });
        } else {
          // Draw elements directly (wait for all images to load)
          (async () => {
            await drawElements(ctx, elements, width, height);
            const texture = new THREE.CanvasTexture(canvas);
            texture.needsUpdate = true;
            resolve(texture);
          })();
        }
      });
    },
    [drawElements]
  );

  // Initialize Three.js scene
  useEffect(() => {
    const mountElement = mountRef.current;
    if (!mountElement) {
      return;
    }

    // Initialize Three.js scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf5f5f5);

    const camera = new THREE.PerspectiveCamera(
      50,
      mountElement.clientWidth / mountElement.clientHeight,
      0.1,
      1000
    );
    // Position camera to view the mug from the preferred angle
    camera.position.set(-162.59, -50.96, 3.61);
    camera.rotation.set(1.5001, -1.2664, 1.4967);
    camera.lookAt(0, 0, 0);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(mountElement.clientWidth, mountElement.clientHeight);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    // Disable tone mapping to preserve bright colors (matches 2D canvas appearance)
    renderer.toneMapping = THREE.NoToneMapping;
    renderer.toneMappingExposure = 1.0;
    mountElement.appendChild(renderer.domElement);

    // Add bright, even lighting to match 2D canvas appearance
    // Very high ambient light ensures colors appear as bright as in 2D view
    const ambientLight = new THREE.AmbientLight(0xffffff, 2.0);
    scene.add(ambientLight);

    // Main directional light - very bright for even illumination
    const directionalLight = new THREE.DirectionalLight(0xffffff, 1.5);
    directionalLight.position.set(80, 50, 100); // From camera's direction, above eye level
    directionalLight.castShadow = false; // Disable shadows for cleaner look
    scene.add(directionalLight);

    // Additional fill light from opposite side for even illumination
    const fillLight = new THREE.DirectionalLight(0xffffff, 1.0);
    fillLight.position.set(-60, 40, 60); // From opposite side
    scene.add(fillLight);

    // Additional top light for even more brightness
    const topLight = new THREE.DirectionalLight(0xffffff, 0.8);
    topLight.position.set(0, 100, 0); // From top
    scene.add(topLight);

    // Set sceneRef synchronously so mesh creation effect can use it immediately
    sceneRef.current = { scene, camera, renderer, controls: null };

    // Store wheel handler reference for cleanup
    const wheelHandlerRef: { current: ((event: WheelEvent) => void) | null } = {
      current: null,
    };

    // Add orbit controls asynchronously and update sceneRef
    import("three/examples/jsm/controls/OrbitControls.js")
      .then((module) => {
        const { OrbitControls } = module;
        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controls.dampingFactor = 0.05;
        controls.enablePan = false;
        controls.minDistance = 100;
        controls.maxDistance = 400;

        // Toggle between old and new zoom behavior
        if (USE_MOUSE_POSITION_ZOOM) {
          // NEW BEHAVIOR: Disable OrbitControls zoom - we handle it with custom wheel handler
          controls.enableZoom = false;
        } else {
          // OLD BEHAVIOR: Use OrbitControls default zoom
          controls.enableZoom = true;
          // Enable zooming towards cursor position if supported
          if ("dollyToCursor" in controls) {
            (controls as any).dollyToCursor = true;
          }
        }

        // Set target to match preferred view
        controls.target.set(0, 0, 0);
        // Restore preferred camera position and rotation
        camera.position.set(-162.59, -50.96, 3.61);
        camera.rotation.set(1.5001, -1.2664, 1.4967);
        controls.update();

        // Custom wheel handler for zooming towards mouse cursor (only if enabled)
        if (USE_MOUSE_POSITION_ZOOM) {
          wheelHandlerRef.current = (event: WheelEvent) => {
            if (!sceneRef.current) {
              return;
            }

            // Prevent default scroll behavior and stop all propagation
            event.preventDefault();
            event.stopPropagation();
            // Don't use stopImmediatePropagation - let other handlers on canvas run if needed

            const {
              camera: cam,
              renderer: rend,
              scene: scn,
              controls,
            } = sceneRef.current;

            if (!controls) {
              return;
            }

            const rect = rend.domElement.getBoundingClientRect();

            // Get mouse position in normalized device coordinates (-1 to +1)
            const mouse = new THREE.Vector2();
            mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
            mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

            // Create raycaster to find point under cursor
            const raycaster = new THREE.Raycaster();
            raycaster.setFromCamera(mouse, cam);

            // Try to intersect with objects in the scene
            const intersects = raycaster.intersectObjects(scn.children, true);
            let zoomPoint: THREE.Vector3;

            if (intersects.length > 0) {
              // Use the first intersection point
              zoomPoint = intersects[0].point;
            } else {
              // If no intersection, calculate point on a plane perpendicular to camera view
              const distance = cam.position.distanceTo(controls.target);
              const direction = new THREE.Vector3();
              cam.getWorldDirection(direction);
              const planePoint = new THREE.Vector3()
                .copy(cam.position)
                .add(direction.multiplyScalar(distance));

              // Calculate camera right and up vectors from view matrix
              const worldPosition = new THREE.Vector3();
              const cameraRight = new THREE.Vector3();
              const cameraUp = new THREE.Vector3();

              // Get camera's right vector (X-axis of camera's local space)
              cameraRight.setFromMatrixColumn(cam.matrixWorld, 0);
              cameraRight.normalize();

              // Get camera's up vector (Y-axis of camera's local space)
              cameraUp.setFromMatrixColumn(cam.matrixWorld, 1);
              cameraUp.normalize();

              // Calculate world position based on mouse offset
              const fov = (cam.fov * Math.PI) / 180;
              const height = 2 * Math.tan(fov / 2) * distance;
              const width = height * cam.aspect;

              worldPosition.copy(planePoint);
              worldPosition.add(
                cameraRight.multiplyScalar(mouse.x * width * 0.5)
              );
              worldPosition.add(
                cameraUp.multiplyScalar(mouse.y * height * 0.5)
              );

              zoomPoint = worldPosition;
            }

            // Calculate zoom factor
            const zoomSpeed = 0.1;
            const delta = event.deltaY > 0 ? 1 + zoomSpeed : 1 - zoomSpeed;
            const currentDistance = cam.position.distanceTo(controls.target);
            const newDistance = currentDistance * delta;

            // Clamp to min/max distance
            const clampedDistance = Math.max(
              controls.minDistance,
              Math.min(controls.maxDistance, newDistance)
            );

            // Calculate the ratio of zoom change
            const zoomRatio = clampedDistance / currentDistance;

            // Calculate vectors from zoom point to current camera and target
            const cameraOffset = new THREE.Vector3().subVectors(
              cam.position,
              zoomPoint
            );
            const targetOffset = new THREE.Vector3().subVectors(
              controls.target,
              zoomPoint
            );

            // Scale both offsets by the zoom ratio
            cameraOffset.multiplyScalar(zoomRatio);
            targetOffset.multiplyScalar(zoomRatio);

            // Calculate new positions
            const newPosition = new THREE.Vector3()
              .copy(zoomPoint)
              .add(cameraOffset);
            const newTarget = new THREE.Vector3()
              .copy(zoomPoint)
              .add(targetOffset);

            // Update camera and controls
            cam.position.copy(newPosition);
            controls.target.copy(newTarget);
            controls.update();
          };

          // Add wheel event listener on both capture and bubble phases
          if (wheelHandlerRef.current) {
            // Use capture phase to catch event before it bubbles to parent
            renderer.domElement.addEventListener(
              "wheel",
              wheelHandlerRef.current,
              { passive: false, capture: true }
            );
            // Also add to bubble phase as backup
            renderer.domElement.addEventListener(
              "wheel",
              wheelHandlerRef.current,
              { passive: false }
            );
          }
        } else {
          // OLD BEHAVIOR: No custom handler, use OrbitControls default
          wheelHandlerRef.current = null;
        }

        // Update sceneRef with controls
        if (sceneRef.current) {
          sceneRef.current.controls = controls;
        }
      })
      .catch(() => {
        // OrbitControls not available
      });

    // Animation loop
    let animationId: number;
    const animate = () => {
      animationId = requestAnimationFrame(animate);
      if (sceneRef.current?.controls) {
        sceneRef.current.controls.update();
      }
      if (sceneRef.current) {
        sceneRef.current.renderer.render(
          sceneRef.current.scene,
          sceneRef.current.camera
        );
      }
    };
    animate();

    // Handle resize
    const handleResize = () => {
      if (!mountElement) return;
      camera.aspect = mountElement.clientWidth / mountElement.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(mountElement.clientWidth, mountElement.clientHeight);
    };
    window.addEventListener("resize", handleResize);

    // Prevent page scrolling when wheel is used over the container (but allow canvas events)
    // Only needed when using custom zoom handler; OrbitControls handles its own events
    const handleContainerWheel = (event: WheelEvent) => {
      // Check if event path includes the canvas element
      const path = event.composedPath ? event.composedPath() : [];
      const hasCanvas = path.some(
        (node) => node instanceof HTMLElement && node.tagName === "CANVAS"
      );

      // Also check target directly
      const target = event.target as HTMLElement;
      const isCanvas = target.tagName === "CANVAS";

      // If it's from the canvas, let it through to the zoom handler
      // Otherwise, prevent scrolling
      if (!isCanvas && !hasCanvas) {
        event.preventDefault();
        event.stopPropagation();
        // Don't use stopImmediatePropagation here - let canvas handler run
      }
    };

    // Only add container handler when using custom zoom (to prevent page scroll)
    // When using OrbitControls default, it handles everything
    if (USE_MOUSE_POSITION_ZOOM) {
      // Only add to bubble phase - let capture phase handlers (like canvas) run first
      mountElement.addEventListener("wheel", handleContainerWheel, {
        passive: false,
      });
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      if (mountElement && USE_MOUSE_POSITION_ZOOM) {
        mountElement.removeEventListener("wheel", handleContainerWheel);
      }
      if (wheelHandlerRef.current && renderer.domElement) {
        renderer.domElement.removeEventListener(
          "wheel",
          wheelHandlerRef.current,
          { capture: true } as any
        );
        renderer.domElement.removeEventListener(
          "wheel",
          wheelHandlerRef.current
        );
      }
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
      if (
        mountElement &&
        renderer.domElement &&
        mountElement.contains(renderer.domElement)
      ) {
        mountElement.removeChild(renderer.domElement);
      }
      renderer.dispose();
      if (sceneRef.current?.controls) {
        sceneRef.current.controls.dispose();
      }
    };
  }, []);

  useEffect(() => {
    if (!sceneRef.current) {
      return;
    }

    const { scene } = sceneRef.current;

    // Remove existing product mesh
    if (sceneRef.current.productMesh) {
      scene.remove(sceneRef.current.productMesh);
      if (sceneRef.current.productMesh.geometry) {
        sceneRef.current.productMesh.geometry.dispose();
      }
      if (sceneRef.current.productMesh.material) {
        if (Array.isArray(sceneRef.current.productMesh.material)) {
          sceneRef.current.productMesh.material.forEach((mat) => mat.dispose());
        } else {
          sceneRef.current.productMesh.material.dispose();
        }
      }
    }

    // Canvas size presets (matching DesignCanvas)
    const canvasSizes: Record<string, { width: number; height: number }> = {
      mug: { width: 800, height: 600 },
      bottle: { width: 600, height: 800 },
      tshirt: { width: 1000, height: 1200 },
      poster: { width: 1200, height: 1800 },
    };

    // Always use preset canvas size (matching DesignCanvas)
    // Elements are stored in preset coordinate space, so canvas must match
    const presetSize = canvasSizes[canvasSize] || canvasSizes.mug;

    // Render design to texture using preset canvas dimensions (matching DesignCanvas)
    // This ensures elements are positioned correctly as they're stored in preset coordinate space
    renderDesignToTexture(
      presetSize.width,
      presetSize.height,
      elements,
      backgroundColor,
      backgroundImage
    )
          .then((texture) => {
            if (!sceneRef.current) {
              return;
            }

            // For cylinder wrapping, use ClampToEdgeWrapping to prevent texture repetition
            texture.wrapS = THREE.RepeatWrapping;
            texture.wrapT = THREE.ClampToEdgeWrapping;
            // Flip Y to correct orientation (Three.js uses bottom-left origin, canvas uses top-left)
            texture.flipY = true;

            // Set texture repeat and offset
            texture.repeat.set(1, 1);
            texture.offset.set(0, 0);

            // Make texture crisp and sharp (no mipmaps for maximum sharpness)
            texture.minFilter = THREE.LinearFilter;
            texture.magFilter = THREE.LinearFilter;
            texture.generateMipmaps = false;

            let productMesh: THREE.Mesh;

            switch (canvasSize) {
              case "mug": {
                // Create mug shape (cylinder with handle)
                const mugGroup = new THREE.Group();

                // Main body (cylinder) - oriented vertically (default Y-axis)
                // Radius 40, height 80, 32 segments for smooth appearance
                const bodyGeometry = new THREE.CylinderGeometry(40, 40, 80, 32);
                // Create material array: [side, top, bottom]
                // Only the side (index 0) gets the texture
                // Top face (index 1) is transparent to show hollow interior
                const bodyMaterials = [
                  // Side material with texture - realistic ceramic mug with reflections
                  // Using MeshPhysicalMaterial for realistic reflections while keeping colors bright
                  new THREE.MeshPhysicalMaterial({
                    map: texture,
                    roughness: 0.2, // Low roughness for glossy ceramic surface with reflections
                    metalness: 0.0, // No metalness for ceramic look
                    clearcoat: 0.8, // High clearcoat for realistic glossy finish
                    clearcoatRoughness: 0.1, // Smooth clearcoat for sharp reflections
                    color: 0xffffff, // White base color to preserve texture colors
                    side: THREE.DoubleSide,
                  }),
                  // Top material - transparent to show hollow interior
                  new THREE.MeshPhysicalMaterial({
                    transparent: true,
                    opacity: 0, // Fully transparent to show hollow interior
                    side: THREE.DoubleSide,
                  }),
                  // Bottom material without texture - white ceramic
                  new THREE.MeshPhysicalMaterial({
                    color: 0xffffff,
                    roughness: 0.2,
                    metalness: 0.0,
                    clearcoat: 0.8,
                    clearcoatRoughness: 0.1,
                  }),
                ];
                const bodyMesh = new THREE.Mesh(bodyGeometry, bodyMaterials);

                // Inner cylinder to show hollow interior and wall thickness
                // Smaller radius (35) to show wall thickness of 5 units
                // Slightly shorter height (79.5) to prevent z-fighting with outer bottom
                const innerGeometry = new THREE.CylinderGeometry(
                  35,
                  35,
                  79.5,
                  32
                );
                // Material array for inner cylinder: [side, top, bottom]
                const innerMaterials = [
                  // Inner side material - white interior wall with ceramic finish
                  new THREE.MeshPhysicalMaterial({
                    color: 0xffffff, // White interior
                    roughness: 0.2,
                    metalness: 0.0,
                    clearcoat: 0.8,
                    clearcoatRoughness: 0.1,
                    side: THREE.DoubleSide,
                  }),
                  // Top face - transparent (open)
                  new THREE.MeshPhysicalMaterial({
                    transparent: true,
                    opacity: 0,
                    side: THREE.DoubleSide,
                  }),
                  // Bottom face - match inner side material to avoid empty look
                  new THREE.MeshPhysicalMaterial({
                    color: 0xffffff, // Same white as inner side
                    roughness: 0.2,
                    metalness: 0.0,
                    clearcoat: 0.8,
                    clearcoatRoughness: 0.1,
                    side: THREE.DoubleSide, // Match inner side material property
                  }),
                ];
                const innerMesh = new THREE.Mesh(innerGeometry, innerMaterials);
                // Position inner cylinder slightly above outer cylinder bottom to prevent z-fighting
                // Height is 79.5, so center at y = 0.25 (slightly above outer cylinder center at y = 0)
                innerMesh.position.y = 0.25; // Slight offset to prevent z-fighting
                innerMesh.rotation.y = -Math.PI / 2; // Match outer cylinder rotation
                mugGroup.add(innerMesh);

                // Top rim ring to cover the gap between outer and inner cylinders
                // Create a ring geometry: inner radius 35, outer radius 40 (gap of 5 units)
                const rimGeometry = new THREE.RingGeometry(35, 40, 32);
                const rimMaterial = new THREE.MeshPhysicalMaterial({
                  color: 0xffffff, // White rim
                  roughness: 0.2,
                  metalness: 0.0,
                  clearcoat: 0.8,
                  clearcoatRoughness: 0.1,
                  side: THREE.DoubleSide,
                });
                const rimMesh = new THREE.Mesh(rimGeometry, rimMaterial);
                // Position at the top of the mug (cylinder height is 80, so top is at y = 40)
                rimMesh.position.y = 40;
                // Rotate to make ring horizontal (lying flat)
                // RingGeometry is created in XY plane (vertical), need to rotate to XZ plane (horizontal)
                // Apply rotations in YXZ order: first Y to match cylinder, then X to lay flat
                rimMesh.rotation.order = "YXZ";
                rimMesh.rotation.y = -Math.PI / 2; // Match cylinder rotation first
                rimMesh.rotation.x = -Math.PI / 2; // Then rotate -90° around X to lay flat
                mugGroup.add(rimMesh);

                // Make sure cylinder is visible
                bodyMesh.visible = true;
                // Rotate cylinder to align texture seam with handle position
                // Default cylinder has seam at the back (negative Z)
                // Handle is at (40, 0, 0) which is the right side (positive X)
                // Rotate -90 degrees around Y to move seam to right side
                bodyMesh.rotation.y = -Math.PI / 2;
                // Position at origin
                bodyMesh.position.set(0, 0, 0);
                mugGroup.add(bodyMesh);

                // Handle (torus) - positioned to the right of the mug
                const handleGeometry = new THREE.TorusGeometry(
                  15,
                  5,
                  8,
                  16,
                  Math.PI
                );
                const handleMaterial = new THREE.MeshPhysicalMaterial({
                  color: 0xffffff, // White handle
                  roughness: 0.2, // Low roughness for glossy ceramic surface
                  metalness: 0.0, // Ceramic look
                  clearcoat: 0.8, // High clearcoat for realistic glossy finish
                  clearcoatRoughness: 0.1, // Smooth clearcoat for sharp reflections
                });
                const handleMesh = new THREE.Mesh(
                  handleGeometry,
                  handleMaterial
                );
                // Position handle at the cylinder edge, 180 degrees from the original position
                // Cylinder radius is 40, handle torus major radius is 15, tube radius is 5
                // Move handle closer to cylinder to ensure it touches
                // Position at -45 to bring it closer (was -50, now closer to cylinder center)
                handleMesh.position.set(-40, 0, 0);
                // Rotate handle to be perpendicular to cylinder
                // TorusGeometry creates a torus in the XY plane by default
                // To make it vertical and perpendicular to a vertical cylinder, rotate around Z axis
                handleMesh.rotation.z = Math.PI / 2;
                // Rotate 180 degrees around vertical (Y) axis to flip handle orientation
                // Then rotate another 180 degrees around Y axis (total 360 degrees = 0)
                handleMesh.rotation.y = Math.PI + Math.PI; // 2 * Math.PI = 0 (full rotation)
                mugGroup.add(handleMesh);

                // Rotate the entire mugGroup to align texture seam with handle
                // Default cylinder seam is at the back (negative Z)
                // Handle is at (40, 0, 0) which is positive X
                // Rotate -90 degrees around Y to move seam to positive X where handle is
                mugGroup.rotation.y = -Math.PI / 2;

                productMesh = mugGroup as any;
                break;
              }

              case "bottle": {
                // Create bottle shape (cylinder with neck)
                const bottleGroup = new THREE.Group();

                // Main body
                const bodyGeometry = new THREE.CylinderGeometry(
                  30,
                  35,
                  100,
                  32
                );
                const bodyMaterial = new THREE.MeshBasicMaterial({
                  map: texture,
                  color: 0xffffff, // White base color (no tinting)
                  transparent: true,
                  opacity: 0.9,
                });
                const bodyMesh = new THREE.Mesh(bodyGeometry, bodyMaterial);
                bodyMesh.position.y = -20;
                bottleGroup.add(bodyMesh);

                // Neck
                const neckGeometry = new THREE.CylinderGeometry(15, 30, 30, 32);
                const neckMaterial = new THREE.MeshStandardMaterial({
                  color: 0xcccccc,
                  roughness: 0.2,
                  metalness: 0.2,
                  transparent: true,
                  opacity: 0.9,
                });
                const neckMesh = new THREE.Mesh(neckGeometry, neckMaterial);
                neckMesh.position.y = 30;
                bottleGroup.add(neckMesh);

                // Cap
                const capGeometry = new THREE.CylinderGeometry(15, 15, 10, 32);
                const capMaterial = new THREE.MeshStandardMaterial({
                  color: 0x888888,
                  roughness: 0.3,
                  metalness: 0.3,
                });
                const capMesh = new THREE.Mesh(capGeometry, capMaterial);
                capMesh.position.y = 50;
                bottleGroup.add(capMesh);

                productMesh = bottleGroup as any;
                break;
              }

              case "tshirt": {
                // Create t-shirt shape (simplified as a flat surface with slight curve)
                const shirtGeometry = new THREE.PlaneGeometry(80, 100, 1, 1);
                // Curve the plane slightly
                const positions = shirtGeometry.attributes.position;
                for (let i = 0; i < positions.count; i++) {
                  const x = positions.getX(i);
                  // y is used implicitly in setZ
                  const z = Math.sin(x / 40) * 5; // Slight curve
                  positions.setZ(i, z);
                }
                positions.needsUpdate = true;
                shirtGeometry.computeVertexNormals();

                const shirtMaterial = new THREE.MeshBasicMaterial({
                  map: texture,
                  color: 0xffffff, // White base color (no tinting)
                  side: THREE.DoubleSide,
                });
                productMesh = new THREE.Mesh(shirtGeometry, shirtMaterial);
                break;
              }

              case "poster": {
                // Create poster shape (flat plane in frame)
                const posterGroup = new THREE.Group();

                // Poster
                const posterGeometry = new THREE.PlaneGeometry(100, 150);
                const posterMaterial = new THREE.MeshBasicMaterial({
                  map: texture,
                  color: 0xffffff, // White base color (no tinting)
                  side: THREE.DoubleSide,
                });
                const posterMesh = new THREE.Mesh(
                  posterGeometry,
                  posterMaterial
                );
                posterGroup.add(posterMesh);

                // Frame
                const frameThickness = 3;
                const frameDepth = 2;
                const frameMaterial = new THREE.MeshStandardMaterial({
                  color: 0x8b4513,
                  roughness: 0.5,
                });

                // Top frame
                const topFrame = new THREE.Mesh(
                  new THREE.BoxGeometry(
                    100 + frameThickness * 2,
                    frameThickness,
                    frameDepth
                  ),
                  frameMaterial
                );
                topFrame.position.set(0, 75 + frameThickness / 2, 0);
                posterGroup.add(topFrame);

                // Bottom frame
                const bottomFrame = new THREE.Mesh(
                  new THREE.BoxGeometry(
                    100 + frameThickness * 2,
                    frameThickness,
                    frameDepth
                  ),
                  frameMaterial
                );
                bottomFrame.position.set(0, -75 - frameThickness / 2, 0);
                posterGroup.add(bottomFrame);

                // Left frame
                const leftFrame = new THREE.Mesh(
                  new THREE.BoxGeometry(frameThickness, 150, frameDepth),
                  frameMaterial
                );
                leftFrame.position.set(-50 - frameThickness / 2, 0, 0);
                posterGroup.add(leftFrame);

                // Right frame
                const rightFrame = new THREE.Mesh(
                  new THREE.BoxGeometry(frameThickness, 150, frameDepth),
                  frameMaterial
                );
                rightFrame.position.set(50 + frameThickness / 2, 0, 0);
                posterGroup.add(rightFrame);

                productMesh = posterGroup as any;
                break;
              }

              default: {
                // Default to mug - create group for hollow mug
                const defaultGroup = new THREE.Group();

                // Outer cylinder
                const bodyGeometry = new THREE.CylinderGeometry(40, 40, 80, 32);
                // Create material array: [side, top, bottom]
                // Top face (index 1) is transparent to show hollow interior
                const bodyMaterials = [
                  // Side material with texture - realistic ceramic mug with reflections
                  // Using MeshPhysicalMaterial for realistic reflections while keeping colors bright
                  new THREE.MeshPhysicalMaterial({
                    map: texture,
                    roughness: 0.2, // Low roughness for glossy ceramic surface with reflections
                    metalness: 0.0, // No metalness for ceramic look
                    clearcoat: 0.8, // High clearcoat for realistic glossy finish
                    clearcoatRoughness: 0.1, // Smooth clearcoat for sharp reflections
                    color: 0xffffff, // White base color to preserve texture colors
                  }),
                  // Top material - transparent to show hollow interior
                  new THREE.MeshPhysicalMaterial({
                    transparent: true,
                    opacity: 0, // Fully transparent to show hollow interior
                    side: THREE.DoubleSide,
                  }),
                  // Bottom material without texture - white ceramic
                  new THREE.MeshPhysicalMaterial({
                    color: 0xffffff,
                    roughness: 0.2,
                    metalness: 0.0,
                    clearcoat: 0.8,
                    clearcoatRoughness: 0.1,
                  }),
                ];
                const bodyMesh = new THREE.Mesh(bodyGeometry, bodyMaterials);
                bodyMesh.rotation.z = Math.PI / 2;
                defaultGroup.add(bodyMesh);

                // Inner cylinder to show hollow interior
                // Smaller radius (35) to show wall thickness of 5 units
                // Slightly shorter height (79.5) to prevent z-fighting with outer bottom
                const innerGeometry = new THREE.CylinderGeometry(
                  35,
                  35,
                  79.5,
                  32
                );
                const innerMaterials = [
                  // Inner side material - white interior wall with ceramic finish
                  new THREE.MeshPhysicalMaterial({
                    color: 0xffffff,
                    roughness: 0.2,
                    metalness: 0.0,
                    clearcoat: 0.8,
                    clearcoatRoughness: 0.1,
                    side: THREE.DoubleSide,
                  }),
                  // Top face - transparent (open)
                  new THREE.MeshPhysicalMaterial({
                    transparent: true,
                    opacity: 0,
                    side: THREE.DoubleSide,
                  }),
                  // Bottom face - match inner side material to avoid empty look
                  new THREE.MeshPhysicalMaterial({
                    color: 0xffffff, // Same white as inner side
                    roughness: 0.2,
                    metalness: 0.0,
                    clearcoat: 0.8,
                    clearcoatRoughness: 0.1,
                    side: THREE.DoubleSide, // Match inner side material property
                  }),
                ];
                const innerMesh = new THREE.Mesh(innerGeometry, innerMaterials);
                // Position inner cylinder slightly above outer cylinder bottom to prevent z-fighting
                // Height is 79.5, so center at y = 0.25 (slightly above outer cylinder center at y = 0)
                innerMesh.position.y = 0.25; // Slight offset to prevent z-fighting
                innerMesh.rotation.z = Math.PI / 2;
                defaultGroup.add(innerMesh);

                // Top rim ring to cover the gap between outer and inner cylinders
                // Inner radius 35, outer radius 40 (gap of 5 units)
                const rimGeometry = new THREE.RingGeometry(35, 40, 32);
                const rimMaterial = new THREE.MeshPhysicalMaterial({
                  color: 0xffffff, // White rim
                  roughness: 0.2,
                  metalness: 0.0,
                  clearcoat: 0.8,
                  clearcoatRoughness: 0.1,
                  side: THREE.DoubleSide,
                });
                const rimMesh = new THREE.Mesh(rimGeometry, rimMaterial);
                // Position at the top of the mug (cylinder height is 80, so top is at y = 40)
                rimMesh.position.y = 40;
                // Rotate to make ring horizontal (lying flat)
                // RingGeometry is created in XY plane (vertical), need to rotate to XZ plane (horizontal)
                // Apply rotations in ZYX order: first Z to match cylinder, then X to lay flat
                rimMesh.rotation.order = "ZYX";
                rimMesh.rotation.z = Math.PI / 2; // Match cylinder rotation first
                rimMesh.rotation.x = -Math.PI / 2; // Then rotate -90° around X to lay flat
                defaultGroup.add(rimMesh);

                productMesh = defaultGroup as any;
                break;
              }
            }

            productMesh.castShadow = true;
            productMesh.receiveShadow = true;
            scene.add(productMesh);
            sceneRef.current.productMesh = productMesh;

            // Update camera to look at the product
            if (sceneRef.current.camera) {
              sceneRef.current.camera.lookAt(0, 0, 0);
            }
          })
          .catch(() => {
            // Error rendering design to texture
          });
  }, [
    elements,
    backgroundColor,
    backgroundImage,
    canvasSize,
    renderDesignToTexture,
  ]);

  return (
    <div
      className="relative w-full h-full"
      style={{
        minHeight: "600px",
        overflow: "hidden",
        touchAction: "none",
      }}
    >
      <div
        ref={mountRef}
        className="w-full h-full"
        style={{
          minHeight: "600px",
          backgroundColor: "#f0f0f0",
          width: "100%",
          height: "100%",
          overflow: "hidden",
          touchAction: "none",
        }}
      />
    </div>
  );
};

export default Product3DPreview;
